"use client";

import {useOrg} from "@/hooks/useOrg";
import {apiFetchWithStatus} from "@/lib/api";
import {GAIL_API} from "@/utils/common/constants/routes";
import {ApprovalLookupError, createSharedApprovalLookup} from "./createSharedApprovalLookup";
import type {ApprovalReferenceKind} from "@/components/chat/approvalReferences";
import {contactDisplayName} from "@/utils/contacts/contactDisplayName";

type EntityKind = Exclude<ApprovalReferenceKind, "script" | "textScript" | "tool">;
const routes: Record<Exclude<EntityKind, "eval">, string> = {
   useCase: "/v1/use_cases", campaign: "/v1/campaigns", contact: "/v1/contacts", contactList: "/v1/contact_lists",
   sequence: "/v1/sequences", redialingRule: "/v1/redialing_rules", assistant: "/.internal/assistants", document: "/.internal/documents",
};
const text = (value: unknown) => typeof value === "string" && value.trim() ? value.trim() : undefined;
const transient = (status: number) => status === 0 || status === 408 || status === 429 || status >= 500;
const useName = createSharedApprovalLookup(async key => {
   const [, kind, id, scriptId] = JSON.parse(key) as [string, EntityKind, string, string | null];
   const route = kind === "eval" ? `/v1/scripts/${encodeURIComponent(scriptId!)}/evals` : routes[kind];
   const {success, status, data: value} = await apiFetchWithStatus<Record<string, unknown>>(`${GAIL_API}${route}/${encodeURIComponent(id)}${kind === "assistant" ? "?includeArchived=true" : ""}`);
   if (!success || !value) throw new ApprovalLookupError(!success && transient(status));
   if (typeof value.id === "string" && value.id.toLowerCase() !== id.toLowerCase()) return undefined;
   if (kind === "contact") return contactDisplayName(value);
   if (kind === "useCase") return text(value.title);
   if (kind === "document") return text(value.fileName);
   return text(value.name);
});

/**
 * Deduplicate by organization, resource type, and ID; never keep another organization's labels.
 * A required lookup identifies an action's target, so it never spends the optional-label budget.
 */
export function useApprovalEntityName(kind: EntityKind, id: string, scriptId?: string, required = false) {
   const {org} = useOrg();
   const scope = kind === "eval" ? scriptId : null;
   const key = org?.id && id && (kind !== "eval" || scope) ? JSON.stringify([org.id, kind, id, scope]) : null;
   return useName(key, required);
}
